/**
 * individualService.js
 *
 * Firestore-native individual/client registry. The core entity — assigned
 * to agents, with full clinical timeline.
 *
 * Collection: organizations/{orgId}/individuals/{individualId}
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  query, where, orderBy, onSnapshot, serverTimestamp,
  arrayUnion, arrayRemove, limit,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { logAuditEvent } from "./auditService";
import { currentOrgId, orgCollection, orgDoc } from "./orgPaths";

// ===== HELPERS =====

function getUser() {
  const u = auth.currentUser;
  if (!u) return null;
  return { uid: u.uid, email: u.email, displayName: u.displayName || u.email };
}

const getOrg = currentOrgId;

/**
 * Look up an agent by name within the organization.
 * Used when supervisor says "assign to Bernadette" during onboarding.
 */
async function resolveAgentByName(name) {
  if (!name) return null;
  try {
    const org = getOrg();
    const q = query(
      collection(db, "users"),
      where("organization", "==", org)
    );
    const snap = await getDocs(q);
    const nameLower = name.toLowerCase();
    
    for (const d of snap.docs) {
      const data = d.data();
      const fullName = (data.displayName || "").toLowerCase();
      const first = (data.firstName || "").toLowerCase();
      const last = (data.lastName || "").toLowerCase();
      
      if (fullName.includes(nameLower) || 
          nameLower.includes(first) || 
          nameLower.includes(last) ||
          `${first} ${last}`.includes(nameLower)) {
        return { uid: d.id, displayName: data.displayName || `${data.firstName} ${data.lastName}` };
      }
    }
    return null;
  } catch (e) {
    console.error("Agent lookup failed:", e);
    return null;
  }
}


// ===== CREATE =====

/**
 * Create a new individual from AI-extracted data.
 * Called after the conversational onboarding confirms the record.
 */
export async function createIndividual(data) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");

    const org = getOrg();
    const id = `ind_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const ref = orgDoc("individuals", id);

    // Resolve agent assignment — if supervisor said "assign to Bernadette", look up the UID
    let assignedUid = user.uid;
    let assignedName = user.displayName;
    
    if (data.assignToAgent) {
      const resolved = await resolveAgentByName(data.assignToAgent);
      if (resolved) {
        assignedUid = resolved.uid;
        assignedName = resolved.displayName;
      }
    }

    const record = {
      id,
      // Demographics
      firstName: data.firstName || "",
      lastName: data.lastName || "",
      dob: data.dob || "",
      gender: data.gender || "",
      phone: data.phone || "",
      email: data.email || "",
      address: data.address || "",
      county: data.county || "",

      // Emergency contact
      emergencyContact: data.emergencyContact || "",
      emergencyPhone: data.emergencyPhone || "",
      emergencyRelation: data.emergencyRelation || "",

      // Clinical
      diagnoses: data.diagnoses || [],
      medications: data.medications || [],
      riskLevel: data.riskLevel || "low",
      allergies: data.allergies || [],

      // Insurance
      medicaidId: data.medicaidId || "",
      insuranceProvider: data.insuranceProvider || "",
      insurancePolicyId: data.insurancePolicyId || "",

      // Program
      program: data.program || "ICM",
      serviceType: data.serviceType || "Intensive Case Management",
      admissionDate: data.admissionDate || new Date().toISOString().split("T")[0],
      contactFrequency: data.contactFrequency || "weekly",

      // ─── Multi-Provider Assignment ───────────────────────────
      // New fields: each provider type gets a dedicated slot
      assignedAgent: data.assignedAgent || assignedUid,
      assignedAgentName: data.assignedAgentName || assignedName,
      assignedClinician: data.assignedClinician || null,
      assignedClinicianName: data.assignedClinicianName || null,
      assignedPrescriber: data.assignedPrescriber || null,
      assignedPrescriberName: data.assignedPrescriberName || null,

      // Legacy fields — kept for backward compatibility with existing queries
      assignedAgents: [assignedUid],
      assignedAgentNames: [assignedName],
      primaryAgent: assignedUid,
      primaryAgentName: assignedName,

      // Organization
      organization: org,
      agentId: user.uid,
      agentName: user.displayName,

      // Metadata
      status: "active",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
      createdByName: user.displayName,

      // Notes / special info from onboarding conversation
      onboardingNotes: data.onboardingNotes || "",
      specialConsiderations: data.specialConsiderations || "",

      // Clinical timeline counters (updated by triggers)
      totalNotes: 0,
      totalSessions: 0,
      lastContactDate: null,
      lastContactType: null,
      lastContactAgent: null,
    };

    await setDoc(ref, record);

    // Also save to localStorage for offline access
    try {
      const localData = JSON.parse(localStorage.getItem("fieldnotes_local_data") || "{}");
      localData.individuals = [...(localData.individuals || []), record];
      localStorage.setItem("fieldnotes_local_data", JSON.stringify(localData));
    } catch (e) { /* non-critical */ }

    await logAuditEvent("individual.create", {
      individualId: id,
      name: `${data.lastName}, ${data.firstName}`,
      agentId: user.uid,
    });

    return { success: true, id, record };
  } catch (e) {
    console.error("Error creating individual:", e);
    return { success: false, error: e.message };
  }
}


// ===== READ =====

/**
 * Get a single individual by ID
 */
export async function getIndividual(id) {
  try {
    const ref = orgDoc("individuals", id);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await logAuditEvent("individual.view", { individualId: id });
      return { id: snap.id, ...snap.data() };
    }
    return null;
  } catch (e) {
    console.error("Error fetching individual:", e);
    return null;
  }
}

/**
 * Subscribe to a single individual (real-time)
 */
export function onIndividual(id, callback) {
  const ref = orgDoc("individuals", id);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    } else {
      callback(null);
    }
  });
}

/**
 * Get all individuals for the current organization
 */
export function onOrgIndividuals(organizationId, callback) {
  // Path scopes by org; no separate `where(organization)` clause needed.
  const q = query(
    orgCollection("individuals", organizationId),
    where("status", "==", "active"),
    orderBy("lastName", "asc")
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error("Org individuals listener error:", error);
    callback([]);
  });
}

/**
 * Get individuals assigned to a specific agent
 */
export function onAgentIndividuals(agentId, organizationId, callback) {
  const q = query(
    orgCollection("individuals", organizationId),
    where("assignedAgents", "array-contains", agentId),
    where("status", "==", "active")
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error("Agent individuals listener error:", error);
    callback([]);
  });
}


// ===== UPDATE =====

/**
 * Update an individual with AI-extracted changes.
 * Merges only the fields that changed.
 */
export async function updateIndividual(id, updates) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");

    const ref = orgDoc("individuals", id);
    await updateDoc(ref, {
      ...updates,
      updatedAt: serverTimestamp(),
      lastModifiedBy: user.uid,
      lastModifiedByName: user.displayName,
    });

    // Also update localStorage
    try {
      const localData = JSON.parse(localStorage.getItem("fieldnotes_local_data") || "{}");
      localData.individuals = (localData.individuals || []).map(ind =>
        ind.id === id ? { ...ind, ...updates } : ind
      );
      localStorage.setItem("fieldnotes_local_data", JSON.stringify(localData));
    } catch (e) { /* non-critical */ }

    await logAuditEvent("individual.update", {
      individualId: id,
      fields: Object.keys(updates),
      agentId: user.uid,
    });

    return { success: true };
  } catch (e) {
    console.error("Error updating individual:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Update medications list
 */
export async function updateMedications(id, medications) {
  return updateIndividual(id, { medications });
}

/**
 * Update risk level
 */
export async function updateRiskLevel(id, riskLevel) {
  return updateIndividual(id, { riskLevel });
}

/**
 * Record a contact (called after note submission)
 */
export async function recordContact(id, contactType, agentName) {
  try {
    const ref = orgDoc("individuals", id);
    await updateDoc(ref, {
      lastContactDate: new Date().toISOString(),
      lastContactType: contactType,
      lastContactAgent: agentName,
      totalNotes: (await getDoc(ref)).data()?.totalNotes + 1 || 1,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error recording contact:", e);
  }
}


// ===== ASSIGNMENT =====

/**
 * Assign an agent to an individual
 */
export async function assignAgent(individualId, agentId, agentName) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedAgent: agentId,
      assignedAgentName: agentName,
      // Also update legacy array fields
      assignedAgents: arrayUnion(agentId),
      assignedAgentNames: arrayUnion(agentName),
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.assign", {
      individualId,
      assignedAgentId: agentId,
      assignedAgentName: agentName,
      providerType: "agent",
    });

    return { success: true };
  } catch (e) {
    console.error("Error assigning agent:", e);
    return { success: false };
  }
}

/**
 * Assign a clinician to an individual
 */
export async function assignClinician(individualId, clinicianId, clinicianName) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedClinician: clinicianId,
      assignedClinicianName: clinicianName,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.assign", {
      individualId,
      assignedClinicianId: clinicianId,
      assignedClinicianName: clinicianName,
      providerType: "clinician",
    });

    return { success: true };
  } catch (e) {
    console.error("Error assigning clinician:", e);
    return { success: false };
  }
}

/**
 * Assign a prescriber to an individual
 */
export async function assignPrescriber(individualId, prescriberId, prescriberName) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedPrescriber: prescriberId,
      assignedPrescriberName: prescriberName,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.assign", {
      individualId,
      assignedPrescriberId: prescriberId,
      assignedPrescriberName: prescriberName,
      providerType: "prescriber",
    });

    return { success: true };
  } catch (e) {
    console.error("Error assigning prescriber:", e);
    return { success: false };
  }
}

/**
 * Remove an agent from an individual
 */
export async function unassignAgent(individualId, agentId, agentName) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedAgent: null,
      assignedAgentName: null,
      assignedAgents: arrayRemove(agentId),
      assignedAgentNames: arrayRemove(agentName),
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.unassign", {
      individualId,
      removedAgentId: agentId,
      providerType: "agent",
    });

    return { success: true };
  } catch (e) {
    console.error("Error unassigning agent:", e);
    return { success: false };
  }
}

/**
 * Remove a clinician from an individual
 */
export async function unassignClinician(individualId) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedClinician: null,
      assignedClinicianName: null,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.unassign", {
      individualId,
      providerType: "clinician",
    });

    return { success: true };
  } catch (e) {
    console.error("Error unassigning clinician:", e);
    return { success: false };
  }
}

/**
 * Remove a prescriber from an individual
 */
export async function unassignPrescriber(individualId) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      assignedPrescriber: null,
      assignedPrescriberName: null,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent("individual.unassign", {
      individualId,
      providerType: "prescriber",
    });

    return { success: true };
  } catch (e) {
    console.error("Error unassigning prescriber:", e);
    return { success: false };
  }
}

/**
 * Get individuals assigned to a specific provider (any role)
 * Checks all three assignment fields + legacy array
 */
export function onProviderIndividuals(providerId, organizationId, callback) {
  // We still use the legacy array-contains for agents (backward compat)
  // For clinicians/prescribers, we need separate queries
  // For now, use org-wide query and filter client-side
  // (Firestore doesn't support OR across different fields in a single query)
  const q = query(
    orgCollection("individuals", organizationId),
    where("status", "==", "active"),
    orderBy("lastName", "asc")
  );
  return onSnapshot(q, (snapshot) => {
    const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const mine = all.filter(ind =>
      ind.assignedAgent === providerId ||
      ind.assignedClinician === providerId ||
      ind.assignedPrescriber === providerId ||
      (ind.assignedAgents || []).includes(providerId)
    );
    callback(mine);
  }, (error) => {
    console.error("Provider individuals listener error:", error);
    callback([]);
  });
}

/**
 * Change primary agent
 */
export async function setPrimaryAgent(individualId, agentId, agentName) {
  return updateIndividual(individualId, {
    primaryAgent: agentId,
    primaryAgentName: agentName,
  });
}


// ===== SOFT DELETE =====

export async function deactivateIndividual(id) {
  try {
    const user = getUser();
    await updateDoc(orgDoc("individuals", id), {
      status: "inactive",
      deactivatedAt: serverTimestamp(),
      deactivatedBy: user?.uid,
    });

    await logAuditEvent("individual.deactivate", { individualId: id });
    return { success: true };
  } catch (e) {
    console.error("Error deactivating individual:", e);
    return { success: false };
  }
}

export async function reactivateIndividual(id) {
  return updateIndividual(id, { status: "active" });
}


// ===== CLINICAL TIMELINE =====

/**
 * Get all notes for an individual (from sharedNotes collection)
 */
export function onIndividualNotes(individualId, callback) {
  const q = query(
    orgCollection("notes"),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc"),
    limit(100)
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
}

/**
 * Get all safety alerts for an individual
 */
export function onIndividualAlerts(individualId, callback) {
  const q = query(
    orgCollection("safetyAlerts"),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
}


// ===== EXPORT =====

/**
 * Get full individual record for PDF export
 * Returns everything: demographics, clinical data, all notes, all alerts
 */
export async function getFullRecordForExport(individualId) {
  try {
    const ind = await getIndividual(individualId);
    if (!ind) throw new Error("Individual not found");

    // Get all notes
    const notesSnap = await getDocs(query(
      orgCollection("notes"),
      where("individualId", "==", individualId),
      orderBy("createdAt", "desc")
    ));
    const notes = notesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Get all safety alerts
    const alertsSnap = await getDocs(query(
      orgCollection("safetyAlerts"),
      where("individualId", "==", individualId),
      orderBy("createdAt", "desc")
    ));
    const alerts = alertsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    await logAuditEvent("individual.export", { individualId });

    return { individual: ind, notes, alerts };
  } catch (e) {
    console.error("Error getting full record:", e);
    return null;
  }
}


// ===== TREATMENT PLANS =====

/**
 * Get treatment plan for an individual (stored on the individual doc)
 */
export async function getTreatmentPlan(individualId) {
  try {
    const ind = await getIndividual(individualId);
    return ind?.treatmentPlan || null;
  } catch (e) {
    console.error("Error getting treatment plan:", e);
    return null;
  }
}

/**
 * Save/update treatment plan on the individual doc
 */
export async function saveTreatmentPlan(individualId, treatmentPlan) {
  try {
    const ref = orgDoc("individuals", individualId);
    await updateDoc(ref, {
      treatmentPlan,
      treatmentPlanUpdatedAt: serverTimestamp(),
    });
    await logAuditEvent("treatmentplan.saved", { individualId });
    return { success: true };
  } catch (e) {
    console.error("Error saving treatment plan:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Add a goal to the treatment plan
 */
export async function addTreatmentGoal(individualId, goal) {
  try {
    const ind = await getIndividual(individualId);
    const plan = ind?.treatmentPlan || { goals: [], createdAt: new Date().toISOString(), status: "active" };
    plan.goals.push({
      id: `goal-${Date.now()}`,
      ...goal,
      status: "in-progress",
      createdAt: new Date().toISOString(),
      linkedNotes: [],
    });
    return await saveTreatmentPlan(individualId, plan);
  } catch (e) {
    console.error("Error adding goal:", e);
    return { success: false };
  }
}

/**
 * Update a specific goal
 */
export async function updateTreatmentGoal(individualId, goalId, updates) {
  try {
    const ind = await getIndividual(individualId);
    const plan = ind?.treatmentPlan;
    if (!plan) return { success: false };
    const goal = plan.goals.find(g => g.id === goalId);
    if (!goal) return { success: false };
    Object.assign(goal, updates);
    return await saveTreatmentPlan(individualId, plan);
  } catch (e) {
    console.error("Error updating goal:", e);
    return { success: false };
  }
}

/**
 * Link a note to a treatment plan goal
 */
export async function linkNoteToGoal(individualId, goalId, noteId, noteSummary) {
  try {
    const ind = await getIndividual(individualId);
    const plan = ind?.treatmentPlan;
    if (!plan) return { success: false };
    const goal = plan.goals.find(g => g.id === goalId);
    if (!goal) return { success: false };
    if (!goal.linkedNotes) goal.linkedNotes = [];
    if (!goal.linkedNotes.find(n => n.noteId === noteId)) {
      goal.linkedNotes.push({ noteId, summary: noteSummary, date: new Date().toISOString() });
    }
    return await saveTreatmentPlan(individualId, plan);
  } catch (e) {
    console.error("Error linking note:", e);
    return { success: false };
  }
}